// src/app/modules/Events/pages/EventOrders.jsx
// Shows orders — either for a specific event (/events/:eventId/orders)
// or all orders across all events (/events/orders)
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Search, ChevronRight } from 'lucide-react';
import * as actions from '../../Dashbord/_redux/dashboard/Actions';
import './events.scss';

const statusColor = { pending: '#D97706', confirmed: '#16A34A', cancelled: '#DC2626', refunded: '#6B7280' };
const statusBg    = { pending: '#FEF3C7', confirmed: '#DCFCE7', cancelled: '#FEE2E2', refunded: '#F3F4F6' };

export function EventOrders() {
  const { eventId } = useParams();
  const isSingleEvent = !!eventId;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { org }   = useSelector(s => ({ org: s.auth.organization }), shallowEqual);
  const { ev, orders, allOrders } = useSelector(s => ({
    ev:        s.dashboard.eventDetail,
    orders:    s.dashboard.eventOrders,
    allOrders: s.dashboard.allOrders,
  }), shallowEqual);

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!org) return;
    if (isSingleEvent) {
      dispatch(actions.fetchAllData('event', eventId));
      dispatch(actions.fetchAllData('event_orders', eventId, { organization_id: org.organization_id, limit: 50 }));
    } else {
      dispatch(actions.fetchAllData('all_orders', org.organization_id, { limit: 50 }));
    }
  }, [org, eventId]);

  const displayOrders = isSingleEvent ? (orders || []) : (allOrders || []);
  const filtered = displayOrders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      o.buyer_name?.toLowerCase().includes(q) ||
      o.buyer_email?.toLowerCase().includes(q) ||
      o.order_id?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="ev-page">
      <div className="ev-page__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isSingleEvent && (
            <button className="ev-back" onClick={() => navigate(`/events/${eventId}`)}>
              <ArrowLeft size={16} strokeWidth={1.8} />
            </button>
          )}
          <div>
            <h1 className="ev-page__title">
              {isSingleEvent && ev ? `Orders — ${ev.name}` : 'Tickets & Orders'}
            </h1>
            <p className="ev-page__sub">{filtered.length} order{filtered.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      <div className="ev-filters">
        <div className="ev-search">
          <Search size={14} strokeWidth={1.8} color="#9597A6" />
          <input
            placeholder="Search by name, email or order ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="ev-orders-table">
        <div className="ev-orders-table__head">
          <span>Buyer</span>
          <span>Tier</span>
          <span>Qty</span>
          <span>Amount</span>
          <span>Date</span>
          <span>Status</span>
          <span />
        </div>

        {filtered.length === 0 && (
          <div className="ev-empty">
            <ShoppingBag size={32} strokeWidth={1.4} />
            <p>No orders found.</p>
          </div>
        )}

        {filtered.map(order => (
          <div className="ev-orders-table__row" key={order.order_id}>
            <span>
              <div className="ev-orders-table__buyer">{order.buyer_name || '—'}</div>
              <div className="ev-orders-table__email">{order.buyer_email || ''}</div>
            </span>
            <span>{order.tier_name || '—'}</span>
            <span>{order.quantity ?? 1}</span>
            <span>₦{order.amount ? Number(order.amount).toLocaleString() : '—'}</span>
            <span>{order.created_at ? new Date(order.created_at).toLocaleDateString('en-NG') : '—'}</span>
            <span>
              <span
                className="ev-badge"
                style={{ background: statusBg[order.status] || '#F1F5F9', color: statusColor[order.status] || '#52546A' }}
              >
                {order.status || 'pending'}
              </span>
            </span>
            <span><ChevronRight size={14} strokeWidth={1.8} color="#CACCD8" /></span>
          </div>
        ))}
      </div>
    </div>
  );
}
